import React, { useCallback, useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, Play, Square, CalendarDays, Plane, Trash2, Check, X } from "lucide-react";

const ABSENCE_LABELS = { vacation: "Ferien", sick: "Krankheit", military: "Militär", accident: "Unfall", unpaid: "Unbezahlter Urlaub", other: "Andere" };
const monthNow = () => new Date().toISOString().slice(0, 7);

function ClockCard({ companyId, employees, onChanged }) {
  const [empId, setEmpId] = useState("");
  const [status, setStatus] = useState(null);
  const [breakMin, setBreakMin] = useState("30");
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async (id) => {
    if (!id) { setStatus(null); return; }
    try {
      const r = await apiClient.get(`/companies/${companyId}/work-time/status`, { params: { employee_id: id } });
      setStatus(r.data);
    } catch { setStatus(null); }
  }, [companyId]);

  useEffect(() => { loadStatus(empId); }, [empId, loadStatus]);

  const act = async (action) => {
    setBusy(true);
    try {
      if (action === "in") {
        await apiClient.post(`/companies/${companyId}/work-time/clock-in`, { employee_id: empId });
        toast.success("Eingestempelt");
      } else {
        const r = await apiClient.post(`/companies/${companyId}/work-time/clock-out`, { employee_id: empId, break_minutes: Number(breakMin) || 0 });
        toast.success(`Ausgestempelt — ${r.data.hours} Std erfasst`);
      }
      await loadStatus(empId);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="worktime-clock-card">
      <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3"><Clock className="h-4 w-4" /> Stempeluhr</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label>Mitarbeiter</Label>
          <select className="border border-slate-200 rounded-md px-2 py-1.5 text-sm min-w-[220px]" value={empId}
            onChange={(e) => setEmpId(e.target.value)} data-testid="worktime-clock-employee-select">
            <option value="">wählen…</option>
            {employees.map((e) => <option key={e._id} value={e._id}>{e.first_name} {e.last_name}</option>)}
          </select>
        </div>
        {status?.running ? (
          <>
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5" data-testid="worktime-running-badge">
              Eingestempelt seit {status.entry.start_time} ({formatDate(status.entry.date)})
            </div>
            <div className="space-y-1">
              <Label>Pause (Min)</Label>
              <Input type="number" min="0" className="w-24" value={breakMin} onChange={(e) => setBreakMin(e.target.value)} data-testid="worktime-break-input" />
            </div>
            <Button disabled={busy} onClick={() => act("out")} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="worktime-clock-out-btn">
              <Square className="h-4 w-4 mr-1" /> Ausstempeln
            </Button>
          </>
        ) : (
          <Button disabled={busy || !empId} onClick={() => act("in")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="worktime-clock-in-btn">
            <Play className="h-4 w-4 mr-1" /> Einstempeln
          </Button>
        )}
      </div>
    </Card>
  );
}

function ManualEntryForm({ companyId, employees, onChanged }) {
  const [f, setF] = useState({ employee_id: "", date: new Date().toISOString().slice(0, 10), start_time: "08:00", end_time: "17:00", break_minutes: "30", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.employee_id) { toast.error("Mitarbeiter wählen"); return; }
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/work-time/entries`, { ...f, break_minutes: Number(f.break_minutes) || 0 });
      toast.success("Zeit erfasst");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erfassung fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2 text-sm" data-testid="worktime-manual-form">
      <select className="border border-slate-200 rounded-md px-2 py-1.5" value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="worktime-manual-employee">
        <option value="">Mitarbeiter…</option>
        {employees.map((e) => <option key={e._id} value={e._id}>{e.first_name} {e.last_name}</option>)}
      </select>
      <SwissDateField className="w-36" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="worktime-manual-date" />
      <Input type="time" className="w-28" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} data-testid="worktime-manual-start" />
      <Input type="time" className="w-28" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} data-testid="worktime-manual-end" />
      <Input type="number" min="0" placeholder="Pause Min" className="w-24" value={f.break_minutes} onChange={(e) => setF({ ...f, break_minutes: e.target.value })} data-testid="worktime-manual-break" />
      <Input placeholder="Notiz" className="w-40" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="worktime-manual-note" />
      <Button type="submit" size="sm" disabled={busy} data-testid="worktime-manual-save">Erfassen</Button>
    </form>
  );
}

function AbsenceForm({ companyId, employees, onChanged }) {
  const [f, setF] = useState({ employee_id: "", type: "vacation", start_date: "", end_date: "", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.employee_id || !f.start_date || !f.end_date) { toast.error("Mitarbeiter und Zeitraum angeben"); return; }
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/absences`, f);
      toast.success("Absenz beantragt");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2 text-sm" data-testid="absence-form">
      <select className="border border-slate-200 rounded-md px-2 py-1.5" value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="absence-employee">
        <option value="">Mitarbeiter…</option>
        {employees.map((e) => <option key={e._id} value={e._id}>{e.first_name} {e.last_name}</option>)}
      </select>
      <select className="border border-slate-200 rounded-md px-2 py-1.5" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="absence-type">
        {Object.entries(ABSENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <SwissDateField className="w-36" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="absence-start" />
      <SwissDateField className="w-36" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="absence-end" />
      <Input placeholder="Notiz" className="w-40" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="absence-note" />
      <Button type="submit" size="sm" disabled={busy} data-testid="absence-save">Beantragen</Button>
    </form>
  );
}

export default function WorkTime() {
  const companyId = localStorage.getItem("demoCompanyId");
  const [employees, setEmployees] = useState([]);
  const [month, setMonth] = useState(monthNow());
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filterEmp, setFilterEmp] = useState("");
  const [absences, setAbsences] = useState([]);
  const [vacation, setVacation] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const empName = (id) => {
    const e = employees.find((x) => x._id === id);
    return e ? `${e.first_name} ${e.last_name}` : id;
  };

  const load = useCallback(async () => {
    if (!companyId) return;
    const year = month.slice(0, 4);
    try {
      const [emp, sum, ent, abs, vac] = await Promise.all([
        apiClient.get(`/companies/${companyId}/employees`),
        apiClient.get(`/companies/${companyId}/work-time/monthly-summary`, { params: { month } }),
        apiClient.get(`/companies/${companyId}/work-time/entries`, { params: { month, ...(filterEmp ? { employee_id: filterEmp } : {}) } }),
        apiClient.get(`/companies/${companyId}/absences`, { params: { year } }),
        apiClient.get(`/companies/${companyId}/work-time/vacation-balance`, { params: { year } }),
      ]);
      setEmployees(emp.data || []);
      setSummary(sum.data);
      setEntries(ent.data.entries || []);
      setAbsences(abs.data.absences || []);
      setVacation(vac.data);
    } catch (e) {
      toast.error("Zeiterfassung konnte nicht geladen werden");
    }
  }, [companyId, month, filterEmp]);

  useEffect(() => { load(); }, [load]);

  const decideAbsence = async (id, action) => {
    setBusyId(id);
    try {
      await apiClient.post(`/companies/${companyId}/absences/${id}/${action}`);
      toast.success(action === "approve" ? "Genehmigt" : "Abgelehnt");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
    finally { setBusyId(null); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Zeiteintrag löschen?")) return;
    try {
      await apiClient.delete(`/companies/${companyId}/work-time/entries/${id}`);
      await load();
    } catch (e) { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div className="space-y-6" data-testid="worktime-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Zeiterfassung Mitarbeiter</h1>
          <p className="mt-1 text-sm text-slate-500">Stempeluhr · Soll/Ist pro Monat · Absenzen & Ferien · Stunden fliessen in die Lohnabrechnung</p>
        </div>
        <div className="space-y-1">
          <Label>Monat</Label>
          <Input type="month" className="w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="worktime-month-input" />
        </div>
      </div>

      <ClockCard companyId={companyId} employees={employees} onChanged={load} />

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="worktime-summary-card">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
          <CalendarDays className="h-4 w-4" /> Monatsblatt {month} ({summary?.workdays ?? "–"} Arbeitstage · Soll-Basis 42 Std/Woche)
        </h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-1.5 pr-3">Mitarbeiter</th><th className="py-1.5 pr-3">Pensum</th>
              <th className="py-1.5 pr-3 text-right">Ist</th><th className="py-1.5 pr-3 text-right">Soll</th>
              <th className="py-1.5 pr-3 text-right">Saldo</th><th className="py-1.5 text-right">Absenz-Tage</th>
            </tr>
          </thead>
          <tbody>
            {(summary?.employees || []).map((s) => (
              <tr key={s.employee_id} className="border-b border-slate-100" data-testid={`worktime-summary-row-${s.employee_id}`}>
                <td className="py-1.5 pr-3 font-medium text-slate-800">{s.name}{s.employment_type === "hourly" && <span className="ml-1 text-[10px] text-slate-400">(Std-Lohn)</span>}</td>
                <td className="py-1.5 pr-3 text-slate-500">{s.pensum_percent}%</td>
                <td className="py-1.5 pr-3 text-right">{s.ist_hours.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right text-slate-500">{s.soll_hours.toFixed(2)}</td>
                <td className={`py-1.5 pr-3 text-right font-medium ${s.saldo_hours >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{s.saldo_hours >= 0 ? "+" : ""}{s.saldo_hours.toFixed(2)}</td>
                <td className="py-1.5 text-right text-slate-500">{s.absence_days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="worktime-entries-card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-semibold text-slate-900">Zeiteinträge {month}</h2>
          <select className="border border-slate-200 rounded-md px-2 py-1 text-sm" value={filterEmp} onChange={(e) => setFilterEmp(e.target.value)} data-testid="worktime-filter-employee">
            <option value="">Alle Mitarbeiter</option>
            {employees.map((e) => <option key={e._id} value={e._id}>{e.first_name} {e.last_name}</option>)}
          </select>
        </div>
        <div className="mb-3 border border-slate-100 rounded-lg p-3 bg-slate-50/50">
          <p className="text-xs text-slate-500 mb-2">Manuell nacherfassen:</p>
          <ManualEntryForm companyId={companyId} employees={employees} onChanged={load} />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {entries.length === 0 && <p className="text-sm text-slate-400">Keine Einträge in diesem Monat.</p>}
          {entries.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-2 text-[13px] border-b border-slate-100 py-1.5" data-testid={`worktime-entry-${e.id}`}>
              <span className="text-slate-700">{formatDate(e.date)} · <span className="font-medium">{empName(e.employee_id)}</span> · {e.start_time}–{e.end_time || "läuft"} {e.break_minutes ? `(${e.break_minutes}′ Pause)` : ""} {e.note ? `· ${e.note}` : ""}</span>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <span className="font-medium">{e.hours != null ? `${e.hours.toFixed(2)} h` : "…"}</span>
                <button onClick={() => deleteEntry(e.id)} className="text-slate-300 hover:text-rose-600" data-testid={`worktime-entry-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="worktime-absences-card">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3"><Plane className="h-4 w-4" /> Absenzen & Ferien {month.slice(0, 4)}</h2>
        <div className="mb-3 border border-slate-100 rounded-lg p-3 bg-slate-50/50">
          <AbsenceForm companyId={companyId} employees={employees} onChanged={load} />
        </div>
        {(vacation?.employees || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {vacation.employees.map((v) => (
              <span key={v.employee_id} className="text-[11px] bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2.5 py-1" data-testid={`vacation-balance-${v.employee_id}`}>
                {v.name}: {v.remaining_days} / {v.entitled_days} Ferientage übrig
              </span>
            ))}
          </div>
        )}
        {absences.length === 0 && <p className="text-sm text-slate-400">Keine Absenzen erfasst.</p>}
        {absences.map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-2 text-[13px] border-b border-slate-100 py-1.5" data-testid={`absence-row-${a.id}`}>
            <span className="text-slate-700">
              <span className="font-medium">{empName(a.employee_id)}</span> · {ABSENCE_LABELS[a.type] || a.type} · {formatDate(a.start_date)} → {formatDate(a.end_date)} ({a.days} Tage) {a.note ? `· ${a.note}` : ""}
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              {a.status === "requested" && (
                <>
                  <span className="text-[11px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">offen</span>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-emerald-700 border-emerald-200" disabled={busyId === a.id} onClick={() => decideAbsence(a.id, "approve")} data-testid={`absence-approve-${a.id}`}><Check className="h-3 w-3" /></Button>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-rose-700 border-rose-200" disabled={busyId === a.id} onClick={() => decideAbsence(a.id, "reject")} data-testid={`absence-reject-${a.id}`}><X className="h-3 w-3" /></Button>
                </>
              )}
              {a.status === "approved" && <span className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">genehmigt</span>}
              {a.status === "rejected" && <span className="text-[11px] bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5">abgelehnt</span>}
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}
